import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Layers, ShieldCheck, Ruler, Hammer, Sparkles, Wrench } from "lucide-react";
import { IMG } from "@/lib/images";

const services = [
  {
    icon: Layers,
    title: "Steinteppich Balkon",
    text: "Fugenlose Balkonbeschichtungen – witterungsbeständig, schnell begehbar, individuelle Farbwahl.",
  },
  {
    icon: ShieldCheck,
    title: "Steinteppich Terrasse",
    text: "Großflächige Terrassenlösungen mit rutschfester Struktur und langlebiger UV-Beständigkeit.",
  },
  {
    icon: Ruler,
    title: "Treppen & Stufen",
    text: "Innen wie außen – nahtlose Treppenbeschichtungen mit präzisem Stufendetail.",
  },
  {
    icon: Hammer,
    title: "Eingangsbereiche & Gehwege",
    text: "Repräsentativ und robust: Eingänge, Wege und Vorplätze in Premium-Optik.",
  },
  {
    icon: Sparkles,
    title: "Innenräume",
    text: "Wohnräume, Bäder, Flure – Steinteppich verbindet Natursteinoptik mit Komfort.",
  },
  {
    icon: Wrench,
    title: "Sanierung & Reparatur",
    text: "Schadstellen, Auffrischungen und Erneuerungen bestehender Beschichtungen.",
  },
];

const process = [
  { n: "01", t: "Beratung", d: "Vor-Ort-Termin, Aufmaß und individuelle Materialempfehlung." },
  { n: "02", t: "Angebot", d: "Transparentes Festpreisangebot mit klarer Leistungsbeschreibung." },
  { n: "03", t: "Vorbereitung", d: "Untergrundprüfung, Grundierung und Abdichtung wenn nötig." },
  { n: "04", t: "Verlegung", d: "Präzise, handwerkliche Ausführung – fugenlos und sauber." },
  { n: "05", t: "Übergabe", d: "Endkontrolle, Pflegehinweise und Garantieübergabe." },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7 },
};

export default function Leistungen() {
  return (
    <div data-testid="leistungen-page">
      {/* Header */}
      <section className="relative pt-32 pb-20 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-6">
            Leistungen
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight max-w-3xl">
            Steinteppich – maßgeschneidert für jeden Bereich.
          </h1>
          <p className="mt-8 max-w-2xl text-stone-600 leading-relaxed">
            Von der Erstberatung bis zur Übergabe begleiten wir Ihr Projekt
            mit handwerklicher Präzision und persönlicher Betreuung.
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200" data-testid="services-grid">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-white p-10 hover:bg-stone-50 transition-colors"
                data-testid={`service-card-${i}`}
              >
                <s.icon size={32} strokeWidth={1.3} className="text-[#1C1C1C] mb-6" />
                <h3 className="font-heading text-xl font-medium mb-3">{s.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
              Ablauf
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              In 5 Schritten zum neuen Boden.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-8" data-testid="process-grid">
            {process.map((p, i) => (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="border-t border-[#1C1C1C] pt-6"
                data-testid={`process-step-${i}`}
              >
                <div className="text-xs tracking-[0.3em] text-stone-500">{p.n}</div>
                <div className="font-heading text-xl font-medium mt-3">{p.t}</div>
                <div className="text-sm text-stone-600 leading-relaxed mt-3">{p.d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
            Lassen Sie uns Ihr Projekt besprechen.
          </h2>
          <p className="mt-6 text-stone-600 max-w-xl mx-auto">
            Kostenlose Beratung, Aufmaß und transparentes Angebot.
          </p>
          <Link
            to="/kontakt"
            data-testid="leistungen-cta"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#1C1C1C] text-white text-sm hover:bg-stone-800 transition-colors"
          >
            Beratung anfragen <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
